import { FaLink, FaTicketAlt } from 'react-icons/fa';
import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toastSuccess, toastError } from '../utils/toasts';
import useQuery from '../hooks/useQuery';
import AdminSectionTitle from '../components/AdminSectionTitle';
import IconInfoCard from '../components/atoms/IconInfoCard';
import LoadingLottie from '../components/atoms/LoadingLottie';
import OrdersTableSkeleton from '../components/OrdersTableSkeleton';
import SelectTicketsTable, { TicketSelection } from '../components/SelectTicketsTable';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import {
  useGetAllTicketsQuery, useGetAudienceDiscountsQuery, useCreateOrderMutation, RootState,
  selectReservation, clearReservation, useGetCurrentAgentQuery,
} from '../store';
import TicketDTO from '../models/ticket';
import Accordion from '../components/atoms/Accordion';
import SectionTitle from '../components/atoms/SectionTitle';
import Input from '../components/atoms/Input';
import RadioButton from '../components/atoms/RadioButton';
import { EventAudienceDiscountDTO, EventPublicStatus } from '../models/event';
import GotCoupon from '../components/GotCoupon';
import CardFrame from '../components/atoms/CardFrame';
import Button from '../components/atoms/Button';
import CouponDTO from '../types/Coupon';
import OrderSummary from '../components/OrderSummary';
import useGetUserRole from '../hooks/useGetUserRole';
import { OrderItemData } from '../models/order';
import { OrderData, PayWayTokenData, NavePaymentData } from '../store/apis/orders';
import { PaymentType, PaymentProvider } from '../models/payment';
import { NaveCheckoutResult } from '../components/NaveCheckout';
import { CheckoutPaymentOptions, shouldShowSubmitButton } from '../components/checkout';
import useSendWhatsapp from '../hooks/useSendWhatsapp';
import generateWhatsappMessage from '../utils/generateWhatsappMessage';
import Switch from '../components/atoms/Switch';
import copyLinkToClipboard from '../utils/copyLinkToClipboard';
import CustomModal from '../components/atoms/CustomModal';
import useGetUserEventData from '../hooks/useGetUserEventData';
import useIsBackoffice from '../hooks/useIsBackoffice';
import Carrousel from '../components/atoms/Carrousel/Carrousel';
import { parseFriendlyDate } from '../utils/dates';
import Select from '../components/atoms/Select/Select';
import NumeratedSeats from '../components/NumeratedSeats';
import { DisplaySeatDTO } from '../models/seat';
import useIsMobile from '../hooks/useIsMobile';
import { emailRegex, noSpaces } from '../utils/regexs';
import useEventPixel from '../hooks/useEventPixel';
import { trackViewContent, trackAddToCart, trackInitiateCheckout } from '../utils/metaPixelTracking';

interface SimplifiedSalePageProps {
  eventpage?: boolean,
}

const SimplifiedSalePage = ({ eventpage = false }: SimplifiedSalePageProps) => {
  const navigate = useNavigate();
  const query = useQuery();
  const isBackoffice = useIsBackoffice();
  const role = useGetUserRole({ skip: !isBackoffice });
  const { event, eventLoading } = useGetSelectedEvent({ slug: !isBackoffice });
  const { data: eventTickets, isLoading: ticketsLoading } = useGetAllTicketsQuery({ businessId: event?.business_id || '', eventId: event?.id || '', publicSaleAvailable: !isBackoffice }, { skip: !event });
  const { data: AudienceDiscounts } = useGetAudienceDiscountsQuery({
    businessId: event?.business_id || '',
    eventId: event?.id || '',
  }, { skip: !event?.business_id || !event?.id || !event?.has_audience_discounts });
  const reservation = useSelector((state: RootState) => state.orders.reservation);
  const [allTickets, setAllTickets] = useState<TicketDTO[]>([]);
  const [entryTickets, setEntryTickets] = useState<TicketDTO[]>([]);
  const [extraTickets, setExtraTickets] = useState<TicketDTO[]>([]);
  const [ticketSelections, setTicketSelections] = useState<TicketSelection[]>([]);
  const [audienceDiscounts, setAudienceDiscounts] = useState<{ [key: number]: EventAudienceDiscountDTO | null }>({});
  const [appliedCoupon, setAppliedCoupon] = useState<CouponDTO | null>(null);
  const [hasCheckedReservation, setHasCheckedReservation] = useState(false);
  const dispatch = useDispatch();
  const [createOrder, { isLoading: createOrderLoading }] = useCreateOrderMutation();
  const [shareMethod, setShareMethod] = useState<'share_link' | 'send_email'>(isBackoffice ? 'share_link' : 'send_email');
  const [customized, setCustomized] = useState<boolean>(event?.has_audience_discounts || false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [shareData, setShareData] = useState<{ url: string; message: string } | null>(null);
  const userEventData = !isBackoffice ? null : useGetUserEventData(event?.id || '');
  const [totalToPay, setTotalToPay] = useState<number>(0);
  const { data: agent } = useGetCurrentAgentQuery(
    { businessId: event?.business_id || '', eventId: event?.id || '' },
    { skip: !event || !event.allow_manual_payments || isBackoffice },
  );
  const [selectedShowtimeId, setSelectedShowtimeId] = useState<string | null>(null);
  const [selectedSeats, setSelectedSeats] = useState<DisplaySeatDTO[]>([]);
  const [ticketsTableResetKey, setTicketsTableResetKey] = useState<number>(0);
  const isMobile = useIsMobile();
  const [paymentMethod, setPaymentMethod] = useState<PaymentType | null>(null);
  const [paymentProvider, setPaymentProvider] = useState<PaymentProvider | null>(null);
  const [payWayTokenData, setPayWayTokenData] = useState<PayWayTokenData | null>(null);
  const [isPayWayProcessing, setIsPayWayProcessing] = useState(false);
  const [, setNavePaymentData] = useState<NavePaymentData | null>(null);
  const [navePaymentRequestId, setNavePaymentRequestId] = useState<string | null>(null);
  const [isNaveProcessing, setIsNaveProcessing] = useState(false);
  const [clickedPay, setClickedPay] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);

  const isSellerGroupCorrect = (ticket: TicketDTO) => {
    if (!ticket.seller_groups || ticket.seller_groups.length === 0) {
      return true;
    }
    return ticket.seller_groups.some((sg) => sg.id === userEventData?.seller_group_id);
  };

  const schema = useMemo(() => {
    const isEmailRequired = !isBackoffice || shareMethod === 'send_email';

    return yup.object().shape({
      tickets: yup.array().of(yup.object().shape({
        full_name: isBackoffice
          ? yup.string()
          : yup.string().required('Debes ingresar un nombre'),
        person_id: isBackoffice
          ? yup.string()
          : yup.string().required('Debes ingresar un DNI'),
      })),
      email: isEmailRequired
        ? yup
          .string()
          .required('Debes ingresar un correo')
          .matches(emailRegex, 'Este correo no está bien')
          .test('no-spaces', 'No se permiten espacios', (value) => !value || noSpaces(value))
        : yup
          .string()
          .matches(emailRegex, 'Este correo no está bien')
          .test('no-spaces', 'No se permiten espacios', (value) => !value || noSpaces(value)),
      emailConfirm: isEmailRequired
        ? yup
          .string()
          .required('Debes ingresar un correo')
          .oneOf([yup.ref('email')], 'Los correos no coinciden')
          .test('no-spaces', 'No se permiten espacios', (value) => !value || noSpaces(value))
        : yup
          .string()
          .oneOf([yup.ref('email')], 'Los correos no coinciden')
          .test('no-spaces', 'No se permiten espacios', (value) => !value || noSpaces(value)),
    });
  }, [isBackoffice, shareMethod]);

  const {
    register, handleSubmit, formState, watch, getValues,
  } = useForm({
    resolver: yupResolver(schema),
    shouldUnregister: true,
    defaultValues: {
      email: '',
      emailConfirm: '',
    },
  });

  useEffect(() => {
    if (isBackoffice) {
      setAllTickets(
        eventTickets?.filter((t) => (role === 'SELLER' && t.is_seller_ticket && t.is_seller_ticket && userEventData && isSellerGroupCorrect(t) && !t.is_only_admin)
          || ((role === 'ADMIN' || role === 'ROOT' || role === 'COPRODUCER'))) || [],
      );
    } else {
      setAllTickets(eventTickets || []);
    }
  }, [eventTickets]);

  useEffect(() => {
    if (selectedShowtimeId) {
      setEntryTickets(allTickets.filter((t) => !t.is_extra && t.showtime_id === selectedShowtimeId));
    } else {
      setEntryTickets(allTickets.filter((t) => !t.is_extra));
    }
    setExtraTickets(allTickets.filter((t) => t.is_extra));
  }, [allTickets, selectedShowtimeId]);

  useEffect(() => {
    if (event && event.has_numerated_seats && !hasCheckedReservation) {
      dispatch(selectReservation());
      setHasCheckedReservation(true);
    }
  }, [event, dispatch, hasCheckedReservation]);

  useEffect(() => {
    if (!event?.has_numerated_seats || !eventTickets || !reservation?.code || !event?.tickets) {
      return;
    }

    const calculatedTickets = selectedSeats.map((s): TicketSelection | null => {
      const ticket = event.tickets.find((t) => t.id === s.ticket_id);
      if (!ticket) {
        console.error(`Ticket not found for seat ID: ${s.id}, ticket ID: ${s.ticket_id}`);
        return null;
      }
      return {
        ticket,
        quantity: 1,
        seat_name: s.seat_name,
        seat_id: s.id,
      };
    }).filter((t): t is TicketSelection => t !== null);

    setTicketSelections(calculatedTickets);
  }, [selectedSeats, eventTickets, reservation?.code, event?.tickets]);

  const handleTicketsChange = (selections: TicketSelection[], type: 'entry' | 'extra') => {
    const otherTypeSelections = ticketSelections.filter((item) => (type === 'entry' ? item.ticket.is_extra : !item.ticket.is_extra));
    const expandedSelections = selections.flatMap((selection) => {
      const individualTickets = [];
      for (let i = 0; i < selection.quantity; i += 1) {
        individualTickets.push({
          ticket: selection.ticket,
          quantity: 1,
          seat_id: selection.seat_id,
          seat_name: selection.seat_name,
        });
      }
      return individualTickets;
    });

    setTicketSelections([...otherTypeSelections, ...expandedSelections]);
  };

  const handleCouponChange = (coupon: CouponDTO | null) => {
    setAppliedCoupon(coupon);
  };

  const resetTicketSelections = () => {
    setTicketSelections([]);
    setSelectedSeats([]);
    setAudienceDiscounts({});
    setAppliedCoupon(null);
    setTicketsTableResetKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (showShareModal) {
      resetTicketSelections();
      setTicketsTableResetKey((prev) => prev + 1);
      if (event?.has_numerated_seats) {
        dispatch(clearReservation());
        setSelectedSeats([]);
      }
    }
  }, [showShareModal, event?.has_numerated_seats, dispatch]);

  useEffect(() => {
    setShowPaymentOptions(
      !!(
        event?.allow_manual_payments
        && agent?.phone_number
        && !isBackoffice
        && totalToPay && totalToPay > 0
      ),
    );
  }, [event, agent, isBackoffice, totalToPay]);

  // Initialize event-specific Meta Pixel for standalone route (not embedded in EventPage)
  useEventPixel(event, { enabled: !isBackoffice && !eventpage });

  useEffect(() => {
    if (event && !isBackoffice && !eventpage) {
      trackViewContent({
        content_name: event.name,
        content_type: 'product',
        content_ids: [event.id],
      });
    }
  }, [event?.id]);

  // Memoized order data for amounts calculation (excludes name/DNI to prevent unnecessary API calls)
  // Must be before early return to maintain consistent hook order
  const orderDataForAmounts = useMemo((): OrderData => ({
    email: getValues('email') || '',
    items: ticketSelections.map((item, index) => ({
      name: 'QR', // Placeholder - name/DNI don't affect pricing
      last_name: event?.name || 'QR',
      person_id: '0', // Placeholder - name/DNI don't affect pricing
      ticket_id: item.ticket.id,
      event_seat_id: item.seat_id,
      audience_discount_id: audienceDiscounts[index]?.id,
    })),
    coupon_id: appliedCoupon?.id,
    open_price_amount: 0,
    poll_responses: [],
    is_manual: isBackoffice,
    skip_poll: true,
    referral_code: '',
    from_backoffice: isBackoffice,
    is_seller_order: role === 'SELLER',
    reservation_code: event?.has_numerated_seats ? reservation?.code : undefined,
  }), [ticketSelections, audienceDiscounts, appliedCoupon, event, isBackoffice, role, reservation?.code]);

  if (eventLoading || !event) {
    return <LoadingLottie />;
  }

  // Memoized orderItemData for display purposes (includes name/DNI from form)
  const orderItemData: OrderItemData[] = ticketSelections.map((item, index) => {
    const formIndex = customized ? index : 0;
    const formValues = watch(`tickets.${formIndex}`);
    return {
      name: formValues?.full_name || '',
      last_name: '',
      person_id: formValues?.person_id || '',
      ticket_id: item.ticket.id,
      ticket: item.ticket,
      ticket_extra: item.ticket.is_extra,
      seat_id: item.seat_id,
      seat_name: item.seat_name,
      audience_discount_id: audienceDiscounts[index]?.id,
      audience_discount: audienceDiscounts[index] || undefined,
    };
  });

  const getPaymentProvider = (): PaymentProvider | undefined => {
    const isManual = paymentMethod === PaymentType.MANUAL || totalToPay === 0 || isBackoffice;
    if (isManual) {
      return PaymentProvider.AVANTTI;
    }
    if (paymentProvider === PaymentProvider.NAVE_LINK) {
      return PaymentProvider.NAVE;
    }
    return paymentProvider ?? undefined;
  };

  const getOrderData = (paywayToken?: PayWayTokenData | null, navePayment?: NavePaymentData | null): OrderData => {
    const refCodeFromQuery = query.get('ref') ?? null;
    const refCodeFromStorage = event ? localStorage.getItem(`${event.id}_referral`) : null;
    const refCode = refCodeFromQuery || refCodeFromStorage;

    return {
      email: getValues('email') || '',
      items: orderItemData.map((item) => ({
        name: item.name || 'QR',
        last_name: item.last_name || event?.name || 'QR',
        person_id: item.person_id || '0',
        ticket_id: item.ticket.id,
        event_seat_id: item.seat_id,
        audience_discount_id: item.audience_discount_id,
      })),
      coupon_id: appliedCoupon?.id,
      open_price_amount: 0,
      poll_responses: [],
      is_manual: paymentMethod === PaymentType.MANUAL || isBackoffice || totalToPay === 0,
      skip_poll: true,
      referral_code: !isBackoffice ? refCode || '' : '',
      from_backoffice: isBackoffice,
      is_seller_order: role === 'SELLER',
      reservation_code: event.has_numerated_seats ? reservation?.code : undefined,
      payment_provider: getPaymentProvider(),
      payway_token: getPaymentProvider() === PaymentProvider.PAYWAY ? paywayToken || undefined : undefined,
      nave_payment: getPaymentProvider() === PaymentProvider.NAVE ? navePayment || undefined : undefined,
    };
  };

  const processOrder = (tokenData?: PayWayTokenData | null, navePayment?: NavePaymentData | null) => {
    if (ticketSelections.length === 0 && selectedSeats.length === 0) {
      toastError('No hay entradas seleccionadas');
      return;
    }

    // Validate payment method is selected for non-backoffice users with payment required
    if (!isBackoffice && totalToPay && totalToPay > 0 && !paymentMethod) {
      toastError('Por favor seleccioná un método de pago');
      return;
    }

    // Validate payment provider is selected for online payments
    if (paymentMethod === PaymentType.ONLINE && totalToPay && totalToPay > 0 && !isBackoffice && !paymentProvider) {
      toastError('Por favor seleccioná un medio de pago');
      return;
    }

    if (!isBackoffice && event) {
      const uniqueIds = Array.from(new Set(ticketSelections.map((ts) => ts.ticket.id)));
      const totalItems = ticketSelections.reduce((sum, ts) => sum + ts.quantity, 0);
      const totalValue = ticketSelections.reduce((sum, ts) => sum + ts.ticket.price * ts.quantity, 0);
      trackAddToCart({
        content_name: event.name,
        content_type: 'product',
        content_ids: uniqueIds,
        value: totalValue,
        num_items: totalItems,
      });
      trackInitiateCheckout({
        content_name: event.name,
        content_ids: uniqueIds,
        value: totalValue,
        num_items: totalItems,
      });
    }

    setClickedPay(true);
    const action = createOrder({
      order: getOrderData(tokenData, navePayment),
      businessId: event.business_id,
      eventId: event.id,
    });

    const cleanupAfterOrderSuccess = () => {
      if (event.has_numerated_seats) {
        dispatch(clearReservation());
        setSelectedSeats([]);
      }
      resetTicketSelections();
      setClickedPay(false);
      setPayWayTokenData(null);
      setNavePaymentData(null);
    };

    action
      .unwrap()
      .then((res) => {
        const od = res.data;
        if (od) {
          if (isBackoffice) {
            toastSuccess('Orden creada');
            cleanupAfterOrderSuccess();
            if (shareMethod === 'share_link') {
              const orderUrl = `${window.location.origin}/${event?.slug}/${od.id}`;
              const shareMsg = `Te envío tus entradas para ${event?.name ?? 'el evento'} ${orderUrl}`;
              setShareData({ url: orderUrl, message: shareMsg });
              setShowShareModal(true);
            }
            if (shareMethod === 'send_email') {
              toastSuccess('Se ha enviado el correo electrónico con las entradas');
              resetTicketSelections();
            }
          } else {
            cleanupAfterOrderSuccess();
            if (totalToPay === 0) {
              navigate(`/order-success?id=${od.custom_id}`, { replace: true });
              return;
            }
            // For PayWay, if there's a payment token, the payment was already processed
            if (paymentProvider === PaymentProvider.PAYWAY && tokenData) {
              toastSuccess('¡Pago procesado exitosamente!');
              navigate(`/order-success?id=${od.custom_id}`, { replace: true });
              return;
            }
            // For Nave, if there's payment data with approved status, the payment was already processed
            if (paymentProvider === PaymentProvider.NAVE && navePayment?.status === 'approved') {
              toastSuccess('¡Pago procesado exitosamente!');
              navigate(`/order-success?id=${od.custom_id}`, { replace: true });
              return;
            }
            // For Nave, if we need to show the checkout, get payment request ID from response
            if (paymentProvider === PaymentProvider.NAVE && !navePayment) {
              const naveRequestId = od.payments?.[0]?.remote_id;
              if (naveRequestId) {
                setNavePaymentRequestId(naveRequestId);
                setClickedPay(false);
                return;
              }
            }
            const checkoutLink = od.payments?.[0]?.checkout_link;
            if (!od.is_manual) {
              if (checkoutLink) {
                window.location.href = checkoutLink;
              } else {
                toastError('No se pudo generar el link de pago');
              }
            } else {
              useSendWhatsapp(
                agent?.phone_number || event.phone || '',
                generateWhatsappMessage(event, od, orderItemData, totalToPay ?? 0),
              );
              navigate(`/order-success?id=${od.custom_id}`, { replace: true });
            }
          }
        } else {
          throw new Error('no order data');
        }
      })
      .catch(async (res) => {
        console.error(res);
        setClickedPay(false);
        setPayWayTokenData(null);
        setNavePaymentData(null);
        setNavePaymentRequestId(null);
        if (res?.data?.error === 'no tickets available to fulfill order') {
          toastError('Lo sentimos, las entradas que habías solicitado se agotaron.');
        } else {
          toastError('Se produjo un error al crear la orden. Intenta nuevamente');
        }
      });
  };

  const onSubmit = () => {
    // For PayWay, we need to wait for the token from the form
    if (paymentProvider === PaymentProvider.PAYWAY && paymentMethod === PaymentType.ONLINE && totalToPay && totalToPay > 0 && !isBackoffice) {
      // The PayWay form will handle submitting - don't process here
      // This triggers form validation, but the actual payment is handled by PayWayCheckout
      if (!payWayTokenData) {
        toastError('Por favor completá los datos de la tarjeta');
        return;
      }
      processOrder(payWayTokenData);
      return;
    }
    // For Nave, we need to create the order first to get the payment request ID
    // Then the Nave checkout will be shown
    if (paymentProvider === PaymentProvider.NAVE && paymentMethod === PaymentType.ONLINE && totalToPay && totalToPay > 0 && !isBackoffice) {
      // If we already have a Nave payment request ID, the checkout is showing
      // Don't process again - the checkout handles payment
      if (navePaymentRequestId) {
        return;
      }
      // Create order to get payment request ID - this will show the Nave checkout
      processOrder();
      return;
    }
    processOrder();
  };

  const handlePayWayTokenCreated = (tokenData: PayWayTokenData) => {
    console.log('PayWay Token Created:', tokenData);
    setPayWayTokenData(tokenData);
    // Automatically process the order with the PayWay token
    processOrder(tokenData);
  };

  const handlePayWayError = (error: string) => {
    toastError(error);
    setClickedPay(false);
  };

  const handleNavePaymentComplete = (result: NaveCheckoutResult) => {
    console.log('Nave Payment Complete:', result);
    if (result.success && result.status === 'approved') {
      const navePayment: NavePaymentData = {
        payment_request_id: navePaymentRequestId || '',
        status: result.status,
        payment_id: result.paymentId,
      };
      setNavePaymentData(navePayment);
      // Process the order with Nave payment data
      processOrder(null, navePayment);
    } else if (result.status === 'pending') {
      toastError(result.message || 'El pago está pendiente de confirmación');
      setClickedPay(false);
    } else {
      toastError(result.message || 'El pago no pudo ser procesado');
      setClickedPay(false);
      setNavePaymentRequestId(null);
    }
  };

  const handleNaveError = (error: string) => {
    toastError(error);
    setClickedPay(false);
    setNavePaymentRequestId(null);
  };

  const handleProviderSelect = (
    provider: PaymentProvider | null,
    method: PaymentType | null,
  ) => {
    if (method === PaymentType.MANUAL) {
      setPaymentMethod(PaymentType.MANUAL);
      setPaymentProvider(null);
    } else {
      setPaymentProvider(provider);
      if (paymentMethod !== PaymentType.ONLINE) {
        setPaymentMethod(PaymentType.ONLINE);
      }
    }
  };

  const handleShare = () => {
    if (!shareData) {
      return;
    }

    if (navigator.share && typeof navigator.share === 'function') {
      navigator.share({
        title: `Entradas para ${event?.name}`,
        text: `Te envío tus entradas para ${event?.name ?? 'el evento'}`,
        url: shareData.url,
      }).then(() => {
        setShowShareModal(false);
      }).catch((error) => {
        console.log('Error sharing:', error);
        copyLinkToClipboard(shareData.message);
      });
    } else {
      copyLinkToClipboard(shareData.message);
    }
  };

  const handleCopyToClipboard = () => {
    if (!shareData) {
      return;
    }
    copyLinkToClipboard(shareData.message);
    setShowShareModal(false);
  };

  if (event?.is_approved === false) {
    return (
      <div className='my-6 flex flex-col gap-6'>
        <AdminSectionTitle
          title="Eventos"
          subtitle={`${event?.name} - Venta rápida`}
        />
        <IconInfoCard
          title="Evento no aprobado"
          info="El evento no ha sido aprobado aún"
          icon={FaLink}
          iconColor="text-primary-500"
        />
      </div>
    );
  }

  if (event?.seller_sales_enabled === false && role === 'SELLER') {
    return (
      <div className='my-6 flex flex-col gap-6'>
        <AdminSectionTitle
          title="Eventos"
          subtitle={`${event?.name} - Venta rápida`}
        />
        <IconInfoCard
          title="No se puede generar entradas"
          info="Las ventas de entradas por vendedores no están habilitadas para este evento"
          icon={FaLink}
          iconColor="text-primary-500"
        />
        <div className="min-w-md mx-auto">
          <Button variant="primary" onClick={() => navigate(-1)}>
            Atrás
          </Button>
        </div>
      </div>
    );
  }

  // Computed values for conditional rendering
  const showSubmitButton = shouldShowSubmitButton(
    paymentProvider,
    paymentMethod,
    totalToPay,
    isBackoffice,
    navePaymentRequestId,
  );

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-11 lg:gap-8 ${eventpage ? '' : 'my-6 lg:my-12 lg:px-8'}`}>
      {!eventpage && <>
        {isBackoffice && <div className="col-span-11">
          <AdminSectionTitle
            title="Eventos"
            subtitle={`${event?.name} - Venta rápida`}
          />
        </div>}
        <div className={`hidden xl:flex mb-8 xl:col-span-4 flex-col gap-4 ${event.public_status !== EventPublicStatus.Ready ? 'grayscale' : ''}`}>
          <Carrousel
            images={[{ url: event.banner_image, eventId: event.id }]}
          />
          <div className='glass-surface text-center lg:text-left'>
            <h1 className='text-3xl font-medium'>{event?.name}</h1>
            <span className='text-xl text-text-primary'>{parseFriendlyDate(event?.date || '', true, true)}</span>
          </div>
        </div>
      </>}
      <div className={`mb-4  ${eventpage ? 'lg:col-span-11' : 'lg:col-span-11 xl:col-span-7'} glass-surface flex flex-col gap-4`}>
        {event?.has_showtimes && event?.showtimes && <div className='flex items-center gap-4 mb-4'>
          {!isMobile && <p className='w-full max-w-md font-medium'>Seleccioná una función</p>}
          <Select
            label={isMobile ? 'Seleccioná una función' : ''}
            id="showtime"
            options={event?.showtimes?.filter((showtime) => showtime.active && showtime.ends_at > new Date().toISOString()).sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()).map((showtime) => ({
              label: parseFriendlyDate(showtime.starts_at, true, true),
              value: showtime.id,
            }))}
            onChange={(value) => {
              setSelectedShowtimeId(value.target.value);
              setTicketSelections([]);
              setTicketsTableResetKey((prev) => prev + 1);
            }}
          />
        </div>
        }
        {event?.has_showtimes && !selectedShowtimeId && <div>
          <IconInfoCard
            title="Primero debes elegir una función"
            info="Una vez elegida la función verás entradas disponibles de cada una"
            icon={FaLink}
            iconColor="text-primary-500"
          />
        </div>}
        {event?.has_showtimes && selectedShowtimeId && entryTickets.length === 0 && <div>
          <IconInfoCard
            title="No hay entradas disponibles para esta función"
            info="Selecciona otra función para ver las entradas disponibles"
            icon={FaLink}
            iconColor="text-primary-500"
          />
        </div>}
        {!event.has_numerated_seats && <div>
          {ticketsLoading && <div className="glass-surface"><OrdersTableSkeleton /></div>}
          {(((!event?.has_showtimes && entryTickets.length > 0)) || (event?.has_showtimes && selectedShowtimeId)) && <div>
            <h1 className="text-lg p-1 text-center glass-surface mb-4">Entradas</h1>
            {!ticketsLoading && <SelectTicketsTable
              key={`entry-${ticketsTableResetKey}`}
              tickets={entryTickets}
              onTicketsChange={(updatedTicketsSelection) => handleTicketsChange(updatedTicketsSelection, 'entry')}
            />}
          </div>}
          {!ticketsLoading && entryTickets.length === 0 && (!event?.has_showtimes || !selectedShowtimeId) && <div className="mt-12">
            <IconInfoCard
              title="Aún no hay entradas creadas"
              info="Aquí aparecerán todas las entradas creadas para el evento"
              icon={FaLink}
              iconColor="text-primary-500"
            />
          </div>}
          {(!ticketsLoading && ((!event?.has_showtimes && extraTickets.length > 0) || (event?.has_showtimes && selectedShowtimeId))) && <div className="mt-5">
            <h1 className="text-lg p-1 text-center glass-surface mb-4">Extras</h1>
            <SelectTicketsTable
              key={`extra-${ticketsTableResetKey}`}
              tickets={extraTickets}
              onTicketsChange={(updatedExtrasSelection) => handleTicketsChange(updatedExtrasSelection, 'extra')}
            />
          </div>}
        </div>}

        {event.has_numerated_seats && <div>
          <NumeratedSeats
            event={event}
            businessId={event.business_id}
            selectedSeats={selectedSeats}
            onSelectedSeatsChange={setSelectedSeats}
            tickets={eventTickets ?? []}
          />
        </div>}

        <div>
          <Accordion
            title={`Datos de las entradas${isBackoffice ? ' (opcional)' : ''}`}
            isExpanded={!isBackoffice}
          >
            <>
              {ticketSelections.length === 0
                && <IconInfoCard
                  title="No hay entradas seleccionadas"
                  info="Aquí aparecerán las entradas que selecciones para editar sus datos"
                  icon={FaTicketAlt}
                  iconColor="text-primary-500"
                />}
              <form onSubmit={handleSubmit(onSubmit)}>
                {(ticketSelections.length > 0) && isBackoffice && <div className="mb-4">
                  <Switch
                    label="Personalizar entradas"
                    name="customized"
                    value="customized"
                    checked={customized}
                    onChange={() => setCustomized(!customized)}
                    disabled={event?.has_audience_discounts === true}
                  />
                </div>}
                {(ticketSelections.length > 0) && ticketSelections.map((item, index) => (
                  (customized || index === 0) && <div key={index}>
                    <div>
                      {isBackoffice && (
                        <div className={`flex flex-col mb-4 ${index !== 0 ? 'mt-6' : ''}`}>
                          <SectionTitle
                            index={index}
                            customizeData={true}
                            item={item.ticket}
                            seatName={item.seat_name}
                            event={event}
                          />
                        </div>
                      )}
                      <div className="flex flex-col gap-4">
                        <Input
                          id={`tickets.${index}.full_name`}
                          label={isBackoffice ? 'Nombre y apellido' : 'Nombre y apellido*'}
                          labelAbove
                          {...register(`tickets.${index}.full_name`)}
                          errMsg={formState.errors.tickets?.[index]?.full_name?.message}
                        />
                        <Input
                          id={`tickets.${index}.person_id`}
                          label={isBackoffice ? 'DNI' : 'DNI*'}
                          labelAbove
                          type="number"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          {...register(`tickets.${index}.person_id`)}
                          errMsg={formState.errors.tickets?.[index]?.person_id?.message}
                        />
                        {!isBackoffice && <div className="flex flex-col gap-4">
                          <Input
                            id="email"
                            label="Correo electrónico*"
                            labelAbove
                            {...register('email')}
                            type="email"
                            errMsg={formState.errors.email?.message}
                          />
                          <Input
                            id="emailConfirm"
                            label="Confirmar correo electrónico*"
                            labelAbove
                            {...register('emailConfirm')}
                            type="email"
                            errMsg={formState.errors.emailConfirm?.message}
                          />
                        </div>}
                        {item.ticket.has_audience_discounts && isBackoffice && (
                          <div>
                            <div className="flex flex-row gap-6">
                              {AudienceDiscounts?.data?.filter((discount) => discount.active).map((discount) => (
                                <div key={discount.id}>
                                  <RadioButton
                                    name={`audience_discount_id_${index}`}
                                    value={discount.id}
                                    label={`${discount.name} - (${discount.percentage * 100}%)`}
                                    checked={audienceDiscounts[index]?.id === discount.id}
                                    checkable={true}
                                    onChange={() => {
                                      setAudienceDiscounts({
                                        ...audienceDiscounts,
                                        [index]: audienceDiscounts[index]?.id === discount.id ? null : discount,
                                      });
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                            {audienceDiscounts[index] && (
                              <p>
                                *Requisito: {audienceDiscounts?.[index]?.requirements}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </form>
            </>
          </Accordion>
        </div>
        {ticketSelections.length === 0
          && <CardFrame>
            <h1>Resumen</h1>
            <IconInfoCard
              title="No hay entradas seleccionadas"
              info="Aquí aparecerá el resumen de las diferentes entradas que elijas"
              icon={FaTicketAlt}
              iconColor="text-primary-500"
            />
          </CardFrame>
        }
        {ticketSelections.length > 0
          && <OrderSummary
            order={orderDataForAmounts}
            event={event}
            onCalculatedTotalToPay={(total) => { setTotalToPay(total); }}
            onLoadingTotals={() => { }}
            hasOpenPrice={false}
            items={orderItemData}
            customized={false}
          />}
        {isBackoffice && <CardFrame>
          <h1>Enviar entradas</h1>
          {isBackoffice && <div className="flex flex-col gap-4 mt-4">
            <RadioButton
              name="share_link"
              value="share_link"
              checked={shareMethod === 'share_link'}
              label="Compartir entradas"
              onChange={() => setShareMethod('share_link')}
              descriptionUnchecked="Realizarás el envío de entradas a través de un link"
              labelClassName="text-lg font-medium"
            />
            <RadioButton
              name="send_email"
              value="send_email"
              checked={shareMethod === 'send_email'}
              label="Correo electrónico"
              onChange={() => setShareMethod('send_email')}
              descriptionUnchecked="Se enviarán por correo las entradas"
              labelClassName="text-lg font-medium"
            />
          </div>}
          {shareMethod === 'send_email' && (
            <div className="flex flex-col gap-4 mt-4">
              <Input
                id="email"
                label="Correo electrónico"
                labelAbove
                {...register('email')}
                type="email"
                errMsg={formState.errors.email?.message}
              />
              <Input
                id="email"
                label="Confirmar correo electrónico"
                labelAbove
                {...register('emailConfirm')}
                type="email"
                errMsg={formState.errors.emailConfirm?.message}
              />
            </div>
          )}
        </CardFrame>}
        <div>
          <GotCoupon
            onCouponChange={handleCouponChange}
            loadedCode={null}
            expanded={false}
          />
        </div>
        {/* Payment Options - With Manual Option */}
        {showPaymentOptions && (
          <CheckoutPaymentOptions
            paymentProvider={paymentProvider}
            paymentMethod={paymentMethod}
            showManualOption
            allowMercadoPago={event.allow_mercadopago_payments}
            allowUala={event.allow_uala_payments}
            allowPayWay={event.allow_payway_payments}
            allowNave={false} // nave embedded checkout disabled
            allowNaveLink
            allowZilo={event.allow_zilo_payments}
            disabled={clickedPay}
            onProviderSelect={handleProviderSelect}
            totalToPay={totalToPay}
            isBackoffice={isBackoffice}
            clickedPay={clickedPay}
            isPayWayProcessing={isPayWayProcessing}
            setIsPayWayProcessing={setIsPayWayProcessing}
            handlePayWayTokenCreated={handlePayWayTokenCreated}
            handlePayWayError={handlePayWayError}
            orderItemData={orderItemData}
            navePaymentRequestId={navePaymentRequestId}
            isNaveProcessing={isNaveProcessing}
            setIsNaveProcessing={setIsNaveProcessing}
            handleNavePaymentComplete={handleNavePaymentComplete}
            handleNaveError={handleNaveError}
          />
        )}
        {/* Payment Options - Without Manual Option */}
        {!showPaymentOptions && !isBackoffice && totalToPay && totalToPay > 0 && (
          <CheckoutPaymentOptions
            paymentProvider={paymentProvider}
            paymentMethod={paymentMethod}
            showManualOption={false}
            allowMercadoPago={event.allow_mercadopago_payments}
            allowUala={event.allow_uala_payments}
            allowPayWay={event.allow_payway_payments}
            allowNave={event.allow_nave_payments}
            allowNaveLink
            allowZilo={event.allow_zilo_payments}
            disabled={clickedPay}
            onProviderSelect={handleProviderSelect}
            totalToPay={totalToPay}
            isBackoffice={isBackoffice}
            clickedPay={clickedPay}
            isPayWayProcessing={isPayWayProcessing}
            setIsPayWayProcessing={setIsPayWayProcessing}
            handlePayWayTokenCreated={handlePayWayTokenCreated}
            handlePayWayError={handlePayWayError}
            orderItemData={orderItemData}
            navePaymentRequestId={navePaymentRequestId}
            isNaveProcessing={isNaveProcessing}
            setIsNaveProcessing={setIsNaveProcessing}
            handleNavePaymentComplete={handleNavePaymentComplete}
            handleNaveError={handleNaveError}
          />
        )}
        <div className="flex flex-row gap-4">
          {!eventpage && <Button variant="secondary" onClick={() => navigate(-1)}>
            Atrás
          </Button>}
          {showSubmitButton && (
            <Button variant="primary" onClick={handleSubmit(onSubmit)} loading={createOrderLoading || clickedPay}>
              {isBackoffice ? 'Generar' : 'Pagar'}
            </Button>
          )}
        </div>
      </div>
      <CustomModal
        isOpen={showShareModal}
        onRequestClose={() => setShowShareModal(false)}
        hideCancelButton
      >
        <div className="flex flex-col gap-4 max-w-md">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">¡Entradas generadas!</h2>
            <p className="text-text-secondary mb-4">
              Las entradas se han creado exitosamente para {event?.name}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              onClick={handleShare}
              className="w-full"
            >
              {navigator.share && typeof navigator.share === 'function' ? 'Compartir entradas' : 'Copiar enlace'}
            </Button>

            {navigator.share && typeof navigator.share === 'function' && (
              <Button
                variant="secondary"
                onClick={handleCopyToClipboard}
                className="w-full"
              >
                Copiar al portapapeles
              </Button>
            )}
          </div>

          <div className="text-center">
            <Button
              variant="tertiary"
              onClick={() => {
                setShowShareModal(false);
              }}
              className="text-text-tertiary underline"
            >
              Cerrar sin compartir
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default SimplifiedSalePage;
